import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { Database } from './supabase_database';

// 외부에서 주입된 클라이언트 (모바일 등)
let _overrideClient: SupabaseClient<any, any, any> | null = null;

/**
 * 외부 환경의 Supabase 클라이언트를 주입합니다.
 * 앱 초기화 시 한 번 호출하면, shared의 모든 서비스가 이 클라이언트를 사용합니다.
 *
 * @example
 * // 모바일 앱 초기화 시
 * import { setSupabaseClient } from '@tickitacka/shared';
 * import { supabase } from '@/src/services/supabaseClient';
 * setSupabaseClient(supabase);
 */
export function setSupabaseClient(client: SupabaseClient<any, any, any>) {
  _overrideClient = client;
}

// Lazy initialization - 실제로 사용할 때만 클라이언트 생성
let _supabase: SupabaseClient<Database> | null = null;

function getSupabaseClient(): SupabaseClient<Database> {
  // 외부 주입 클라이언트가 있으면 우선 사용
  if (_overrideClient) {
    return _overrideClient as unknown as SupabaseClient<Database>;
  }

  if (_supabase) {
    return _supabase;
  }

  let supabaseUrl = '';
  let supabaseAnonKey = '';

  // Vite 환경에서는 import.meta.env를 사용
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
    supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
  }

  // 환경 변수가 없을 경우 에러
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error(
      'Supabase 환경 변수가 설정되지 않았습니다. VITE_SUPABASE_URL과 VITE_SUPABASE_ANON_KEY를 확인하세요.',
    );
  }

  _supabase = createClient<Database>(supabaseUrl, supabaseAnonKey);
  return _supabase;
}

// Proxy를 사용하여 실제 사용 시점에 클라이언트 생성
export const supabase = new Proxy({} as SupabaseClient<Database>, {
  get(_, prop) {
    const client = getSupabaseClient();
    const value = client[prop as keyof SupabaseClient<Database>];
    if (typeof value === 'function') {
      return value.bind(client);
    }
    return value;
  },
});
